import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Power,
  PowerOff,
  CheckCircle, 
  XCircle, 
  Clock,
  AlertCircle,
  RefreshCw,
  Calendar,
  User,
  DollarSign,
  Target,
  FileText,
  Eye,
  Crown,
  Star,
  Zap,
  BarChart3,
  Search,
  Award,
  ExternalLink
} from 'lucide-react';
import { 
  Chip,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  TextField,
  FormControl
} from '@mui/material';
import adminApi from '../../api/admin';
import Button from '../../components/common/Button';
import { toast } from 'react-toastify';
import { formatDateForDisplay } from '../../utils/helpers';

const AdminSubscriptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState('');
  const [reason, setReason] = useState('');
  const [endDate, setEndDate] = useState('');

  const {
    data: subscription,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["subscription-details", id],
    queryFn: async () => {
      const res = await adminApi.getSubscriptionDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  // Activate/Deactivate/Suspend subscription mutation
  const updateSubscriptionStatusMutation = useMutation({
    mutationFn: ({ id, action, reason, end_date }) => 
      adminApi.updateSubscriptionStatus(id, action, reason, end_date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-details', id] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      setStatusDialogOpen(false);
      setReason('');
      setEndDate('');
      toast.success(`Subscription ${statusAction} successfully!`);
    },
    onError: (error) => {
      console.error('Failed to update subscription status:', error);
      toast.error(`Failed to ${statusAction} subscription`);
    }
  });

  const handleStatusChange = (action) => {
    setStatusAction(action);
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = (action, reason) => {
    if (subscription) {
      updateSubscriptionStatusMutation.mutate({ 
        id: subscription.id, 
        action, 
        reason,
        end_date: endDate || undefined
      });
    }
  };

  const handleBack = () => {
    navigate('/admin/subscriptions');
  };

  const getStatusConfig = (status) => {
    const statusConfig = {
      active: { color: 'success', label: 'Active', icon: CheckCircle, description: 'Subscription is active and running' },
      pending: { color: 'warning', label: 'Pending', icon: Clock, description: 'Subscription is being processed' },
      cancelled: { color: 'error', label: 'Cancelled', icon: XCircle, description: 'Subscription has been cancelled' },
      suspended: { color: 'warning', label: 'Suspended', icon: AlertCircle, description: 'Subscription is temporarily suspended' },
      expired: { color: 'default', label: 'Expired', icon: XCircle, description: 'Subscription has expired' }
    };
    
    return statusConfig[status] || { color: 'default', label: status, icon: AlertCircle, description: 'Unknown status' };
  };

  const getAvailableActions = (currentStatus) => {
    const actions = [];
    
    switch (currentStatus) {
      case 'active':
        actions.push({ action: 'suspend', label: 'Suspend', icon: PowerOff, color: 'warning' });
        actions.push({ action: 'cancel', label: 'Cancel', icon: XCircle, color: 'error' });
        break;
      case 'suspended':
        actions.push({ action: 'activate', label: 'Activate', icon: Power, color: 'success' });
        actions.push({ action: 'cancel', label: 'Cancel', icon: XCircle, color: 'error' });
        break;
      case 'cancelled':
        actions.push({ action: 'activate', label: 'Reactivate', icon: Power, color: 'success' });
        break;
      case 'pending':
        actions.push({ action: 'activate', label: 'Activate', icon: Power, color: 'success' });
        actions.push({ action: 'cancel', label: 'Cancel', icon: XCircle, color: 'error' });
        break;
      case 'expired':
        actions.push({ action: 'activate', label: 'Renew', icon: Power, color: 'success' });
        break;
      default:
        actions.push({ action: 'activate', label: 'Activate', icon: Power, color: 'success' });
    }
    
    return actions;
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading subscription details: {error?.message}</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Subscriptions
          </Button>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Subscription not found</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Subscriptions
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(subscription.status);
  const availableActions = getAvailableActions(subscription.status);
  const isActive = subscription.status === 'active';

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleBack}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Subscription Details</h1>
            <p className="text-secondary-600">Manage user subscription and status</p>
          </div>
        </div>
        {/* <div className="flex space-x-3">
          {availableActions.map((action) => (
            <Button
              key={action.action}
              variant="outline"
              onClick={() => handleStatusChange(action.action)}
              icon={<action.icon className="w-4 h-4 mr-2" />}
              disabled={updateSubscriptionStatusMutation.isLoading}
              color={action.color}
            >
              {action.label}
            </Button>
          ))}
        </div> */}
      </div>

      {/* Subscription Overview */}
      <Grid container spacing={3} className="mb-6">
        <Grid size={{xs:12, lg: 8}}>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h2 className="text-2xl font-bold text-secondary-900">
                        Subscription #{subscription.id}
                      </h2>
                      <Chip 
                        label={statusConfig.label} 
                        color={statusConfig.color}
                        icon={<statusConfig.icon className="w-4 h-4" />}
                      />
                    </div>
                    <p className="text-secondary-600">{statusConfig.description}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-secondary-600 mb-1">User ID</div>
                    <div className="text-xl font-bold text-secondary-900 flex items-center justify-center">
                      <User className="w-5 h-5 mr-2" />
                      <Link to={`/admin/users/${subscription.user}`} className='text-blue-600 hover:underline font-semibold'>{subscription.user}</Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {subscription.job_postings_count}
                  </div>
                  <div className="text-sm text-secondary-600">Job Postings Used</div>
                  <div className="text-xs text-gray-500">
                    {subscription.job_postings_remaining >= 0 
                      ? `${subscription.job_postings_remaining} remaining` 
                      : 'Unlimited'
                    }
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {subscription.profile_views_count}
                  </div>
                  <div className="text-sm text-secondary-600">Profile Views Used</div>
                  <div className="text-xs text-gray-500">
                    {subscription.profile_views_remaining >= 0 
                      ? `${subscription.profile_views_remaining} remaining` 
                      : 'Unlimited'
                    }
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {subscription.auto_renew ? 'Yes' : 'No'}
                  </div>
                  <div className="text-sm text-secondary-600">Auto Renew</div>
                  <div className="text-xs text-gray-500">
                    {subscription.auto_renew ? 'Automatic' : 'Manual'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, lg: 4}}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">Subscription Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Payment Method</span>
                  <Chip 
                    label={subscription.payment_method} 
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Auto Renew</span>
                  <Chip 
                    label={subscription.auto_renew ? 'Enabled' : 'Disabled'} 
                    color={subscription.auto_renew ? 'success' : 'default'}
                    size="small"
                  />
                </div>
                <Divider />
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Start Date</span>
                  <span className="font-medium text-sm">
                    {formatDateForDisplay(subscription.start_date)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">End Date</span>
                  <span className="font-medium text-sm">
                    {subscription.end_date ? formatDateForDisplay(subscription.end_date) : 'Not set'}
                  </span>
                </div>
                <Divider />
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Created</span>
                  <span className="font-medium text-sm">
                    {formatDateForDisplay(subscription.created_at)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Last Updated</span>
                  <span className="font-medium text-sm">
                    {formatDateForDisplay(subscription.updated_at)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Plan Information */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-secondary-900">Plan Information</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/subscription-plans/${subscription.plan.id}`)}
              icon={<ExternalLink className="w-4 h-4 mr-2" />}
            >
              View Plan Details
            </Button>
          </div>
          
          <Grid container spacing={4}>
            <Grid size={{xs:12, md: 6}}>
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                  {parseFloat(subscription.plan.price) > 50 ? (
                    <Crown className="w-6 h-6 text-white" />
                  ) : (
                    <Star className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-secondary-900">{subscription.plan.name}</h4>
                  <p className="text-secondary-600">{subscription.plan.description}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Price</span>
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-bold">{subscription.plan.price}</span>
                    <span className="text-sm text-gray-500 ml-1">
                      {subscription.plan.currency}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Target Role</span>
                  <Chip 
                    label={subscription.plan.target_role} 
                    color={subscription.plan.target_role === 'professional' ? 'primary' : 'secondary'}
                    size="small"
                    icon={<Target className="w-3 h-3" />}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Plan Status</span>
                  <Chip 
                    label={subscription.plan.is_active ? 'Active' : 'Inactive'} 
                    color={subscription.plan.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </div>
              </div>
            </Grid>
            
            <Grid size={{xs:12, md: 6}}>
              <h5 className="font-semibold mb-3 text-secondary-900">Plan Limits</h5>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-secondary-600">Job Applications</span>
                  <span className="font-medium">
                    {subscription.plan.job_application_limit > 0 
                      ? subscription.plan.job_application_limit 
                      : 'Unlimited'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-secondary-600">Job Postings</span>
                  <span className="font-medium">
                    {subscription.plan.job_posting_limit > 0 
                      ? subscription.plan.job_posting_limit 
                      : 'Unlimited'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-secondary-600">Profile Views</span>
                  <span className="font-medium">
                    {subscription.plan.profile_view_limit > 0 
                      ? subscription.plan.profile_view_limit 
                      : 'Unlimited'
                    }
                  </span>
                </div>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="flex items-center space-x-2">
          {statusAction === 'activate' ? (
            <Power className="w-5 h-5 text-green-500" />
          ) : statusAction === 'suspend' ? (
            <PowerOff className="w-5 h-5 text-orange-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <span>
            {statusAction === 'activate' ? 'Activate' : 
             statusAction === 'suspend' ? 'Suspend' : 'Cancel'} Subscription
          </span>
        </DialogTitle>
        <DialogContent>
          <Typography className="mb-4">
            Are you sure you want to {statusAction} subscription #{subscription.id}?
          </Typography>
          
          <FormControl fullWidth className="mb-3">
            <Typography variant="subtitle2" className="mb-2 text-secondary-700">
              Reason for {statusAction} (optional):
            </Typography>
            <textarea
              minRows={3}
              placeholder={`Enter reason for ${statusAction} this subscription...`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              style={{ minHeight: '80px' }}
            />
          </FormControl>

          {statusAction === 'suspend' && (
            <FormControl fullWidth className="mb-3">
              <Typography variant="subtitle2" className="mb-2 text-secondary-700">
                Suspension end date (optional):
              </Typography>
              <TextField
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
              />
            </FormControl>
          )}

          {statusAction === 'cancel' && (
            <Alert severity="warning" className="mt-2">
              Cancelling this subscription will prevent it from renewing. The user will lose access at the end of the current billing period.
            </Alert>
          )}

          {statusAction === 'suspend' && (
            <Alert severity="warning" className="mt-2">
              Suspending this subscription will temporarily disable access. You can reactivate it later.
            </Alert>
          )}

          {statusAction === 'activate' && (
            <Alert severity="info" className="mt-2">
              Activating this subscription will restore user access immediately.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={() => setStatusDialogOpen(false)}
            disabled={updateSubscriptionStatusMutation.isLoading}
          >
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={() => handleStatusConfirm(statusAction, reason)}
            variant="contained"
            color={
              statusAction === 'activate' ? 'success' : 
              statusAction === 'suspend' ? 'warning' : 'error'
            }
            disabled={updateSubscriptionStatusMutation.isLoading}
            startIcon={
              updateSubscriptionStatusMutation.isLoading ? null : 
              statusAction === 'activate' ? <Power className="w-4 h-4" /> : 
              statusAction === 'suspend' ? <PowerOff className="w-4 h-4" /> : 
              <XCircle className="w-4 h-4" />
            }
          >
            {updateSubscriptionStatusMutation.isLoading 
              ? `${statusAction === 'activate' ? 'Activating' : 
                 statusAction === 'suspend' ? 'Suspending' : 'Cancelling'}...` 
              : `${statusAction === 'activate' ? 'Activate' : 
                 statusAction === 'suspend' ? 'Suspend' : 'Cancel'} Subscription`
            }
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AdminSubscriptionDetails;